import Send from '@mui/icons-material/Send';
import { Box, InputAdornment, IconButton as MuiIconButton } from '@mui/material';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Typography from '@mui/material/Typography';
import { useRef, useState } from 'react';
import { IconButton, TextField, LoadingSpinner } from '../components';
import React from 'react';

declare type ChatApiResponse = {
  answer: string;
  chat_history: string[][];
};

const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [chatHistory, setChatHistory] = useState<string[][]>([]);
  const [sending, setSending] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const fabRef = useRef(null);

  const onSubmit = async () => {
    if (!draft) return;
    setSending(true);
    await sendMessage(draft);
    setDraft('');
    setSending(false);
  };

  const sendMessage = async (message: string) => {
    try {
      const response = await fetch('https://it-chatbot-fed7e26b8f91.herokuapp.com/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question: message, chat_history: chatHistory }),
      });

      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      setErrorMsg(null);

      const data: ChatApiResponse = await response.json();

      setChatHistory(data.chat_history);
    } catch (error) {
      setErrorMsg('Failed to send message. Please try again later.');
    }
  };

  const handleKeyDown = (event: { key: string }) => {
    if (draft && event.key === 'Enter') {
      onSubmit();
    }
  };

  return (
    <>
      {/* Floating Action Button */}
      <div
        ref={fabRef}
        style={{
          position: 'fixed',
          bottom: 16,
          right: 16,
          zIndex: 1299,
        }}>
        <IconButton
          size={open ? 'large' : 'large'} // for now. would like it to shrink when open, but have to reposition popper
          label={open ? 'Close chat' : 'Open chat'}
          aria-label={open ? 'Close chat' : 'Open chat'}
          onClick={() => setOpen((prev) => !prev)}
          icon={open ? 'close' : 'image'}
          imgSrc="/it-chatbot-logo-cropped.png"
          imgAlt="IT Chatbot"
        />
      </div>

      {/* Chat Window using Popper */}
      <Popper
        open={open}
        anchorEl={fabRef.current}
        placement="top-end"
        style={{ zIndex: 1299 }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 16], // space above the FAB
            },
          },
        ]}>
        <Paper
          elevation={8}
          sx={{
            width: '90vw',
            maxWidth: 600,
            height: '70vh',
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            position: 'relative',
            p: 0,
          }}>
          <div
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: 'center',
              width: '100%',
              paddingTop: 16,
              paddingBottom: 8,
            }}>
            <Typography variant="h6" sx={{ margin: 'auto' }}>
              IT Support Chat
            </Typography>
          </div>
          <div style={{ flex: 1, padding: 32, overflow: 'auto' }}>
            {chatHistory.map(([question, answer]) => (
              <Box
                key={question}
                sx={{
                  mb: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                }}>
                <Typography variant="body1" sx={{ color: 'green.main', fontWeight: 'bold' }}>
                  Question:
                </Typography>
                <Typography variant="body1" sx={{ color: 'gray', mb: 1 }}>
                  {question}
                </Typography>
                <Typography variant="body1" sx={{ color: 'blue.main', fontWeight: 'bold' }}>
                  Answer:
                </Typography>
                <Typography variant="body1">{answer}</Typography>
              </Box>
            ))}
          </div>
          {errorMsg && (
            <Typography variant="body2" color="error" sx={{ textAlign: 'center', mb: 1 }}>
              {errorMsg}
            </Typography>
          )}
          <TextField
            // size='small'
            label="How can I help?"
            value={draft}
            onChange={(e: { target: { value: React.SetStateAction<string> } }) =>
              setDraft(e.target.value)
            }
            onKeyDown={handleKeyDown}
            sx={{
              margin: 1,
              size: '34px',
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end" sx={{ marginRight: '10px', color: 'green.main' }}>
                  <MuiIconButton onClick={onSubmit} edge="end" disabled={!draft || sending}>
                    {sending ? <LoadingSpinner /> : <Send sx={{ color: 'green.main' }} />}
                  </MuiIconButton>
                </InputAdornment>
              ),
            }}
          />
        </Paper>
      </Popper>
    </>
  );
};

export default ChatWidget;
